import api from './api';
import API_CONFIG from '../config/api';

class TemplateService {
  // Get all templates with pagination and filtering
  async getTemplates(params = {}) {
    try {
      const response = await api.get('/templates', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching templates:', error);
      throw error;
    }
  }

  // Get template by ID
  async getTemplateById(id) {
    try {
      const response = await api.get(`/templates/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching template:', error);
      throw error;
    }
  }

  // Create new template
  async createTemplate(templateData, file = null) {
    try {
      const formData = new FormData();
      
      // Append template data
      Object.keys(templateData).forEach(key => {
        if (templateData[key] !== null && templateData[key] !== undefined) {
          if (typeof templateData[key] === 'object') {
            formData.append(key, JSON.stringify(templateData[key]));
          } else {
            formData.append(key, templateData[key]);
          }
        }
      });
      
      // Append file if provided
      if (file) {
        formData.append('templateFile', file);
      }
      
      const response = await api.post('/templates', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error creating template:', error);
      throw error;
    }
  }

  // Update template
  async updateTemplate(id, templateData, file = null) {
    try {
      const formData = new FormData();
      
      // Append template data
      Object.keys(templateData).forEach(key => {
        if (templateData[key] !== null && templateData[key] !== undefined) {
          if (typeof templateData[key] === 'object') {
            formData.append(key, JSON.stringify(templateData[key]));
          } else {
            formData.append(key, templateData[key]);
          }
        }
      });
      
      // Append file if provided
      if (file) {
        formData.append('templateFile', file);
      }
      
      const response = await api.put(`/templates/${id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error updating template:', error);
      throw error;
    }
  }

  // Delete template
  async deleteTemplate(id) {
    try {
      const response = await api.delete(`/templates/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting template:', error);
      throw error;
    }
  }

  // Get available contract types
  async getContractTypes() {
    try {
      const response = await api.get('/templates/types/available');
      return response.data;
    } catch (error) {
      console.error('Error fetching contract types:', error);
      throw error;
    }
  }

  // Get available template statuses
  async getTemplateStatuses() {
    try {
      const response = await api.get('/templates/statuses/available');
      return response.data;
    } catch (error) {
      console.error('Error fetching template statuses:', error);
      throw error;
    }
  }

  // Preview template with sample data
  async previewTemplate(id, sampleData = {}) {
    try {
      const response = await api.post(`/templates/${id}/preview`, { sampleData });
      return response.data;
    } catch (error) {
      console.error('Error previewing template:', error);
      throw error;
    }
  }

  // Generate document from template
  async generateDocument(id, contractData, outputFormat = 'Word') {
    try {
      const response = await api.post(`/templates/${id}/generate`, {
        contractData,
        outputFormat
      });
      return response.data;
    } catch (error) {
      console.error('Error generating document:', error);
      throw error;
    }
  }

  // Get template statistics
  async getTemplateStatistics() {
    try {
      const response = await api.get('/templates/statistics/overview');
      return response.data;
    } catch (error) {
      console.error('Error fetching template statistics:', error);
      throw error;
    }
  }

  // Download template file
  async downloadTemplateFile(templateId) {
    try {
      const response = await api.get(`/templates/${templateId}/download`, {
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      console.error('Error downloading template file:', error);
      throw error;
    }
  }

  // Extract placeholders from template content
  extractPlaceholders(content) {
    const placeholderRegex = /\{\{([^}]+)\}\}/g;
    const placeholders = [];
    let match;
    
    while ((match = placeholderRegex.exec(content)) !== null) {
      const placeholderName = match[1].trim();
      if (!placeholders.find(p => p.name === placeholderName)) {
        placeholders.push({
          name: placeholderName,
          description: `Placeholder for ${placeholderName}`,
          required: false,
          defaultValue: '',
          dataType: 'text',
          fieldMapping: ''
        });
      }
    }
    
    return placeholders;
  }

  // Validate template data
  validateTemplateData(data) {
    const errors = {};
    
    if (!data.name || data.name.trim() === '') {
      errors.name = 'Template name is required';
    }
    
    if (!data.applicableTypes || data.applicableTypes.length === 0) {
      errors.applicableTypes = 'At least one contract type is required';
    }
    
    if (!data.templateType) {
      errors.templateType = 'Template type is required';
    }
    
    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  }

  // Format template data for API
  formatTemplateData(data) {
    return {
      name: data.name?.trim(),
      description: data.description?.trim(),
      templateType: data.templateType,
      applicableTypes: Array.isArray(data.applicableTypes) ? data.applicableTypes : 
                      (typeof data.applicableTypes === 'string' ? data.applicableTypes.split(',').map(t => t.trim()) : []),
      placeholders: data.placeholders || [],
      sections: data.sections || [],
      status: data.status || 'Draft',
      tags: Array.isArray(data.tags) ? data.tags : 
            (typeof data.tags === 'string' ? data.tags.split(',').map(t => t.trim()).filter(t => t) : []),
      settings: data.settings || {
        includeClauses: true,
        includeDocuments: false,
        defaultOutputFormat: 'Word',
        pageSettings: {
          orientation: 'Portrait',
          margins: { top: 1, bottom: 1, left: 1, right: 1 },
          fontSize: 12,
          fontFamily: 'Arial'
        }
      }
    };
  }

  // Get sample contract data for preview
  getSampleContractData(contractType = 'Service Agreement') {
    const baseData = {
      contractId: 'CON-2024-001',
      contractName: 'Sample Service Agreement',
      contractType: contractType,
      effectiveDate: '2024-01-15',
      expiryDate: '2024-12-31',
      contractTerm: '12 months',
      contractValue: '50000',
      currency: 'USD',
      contractPurpose: 'Software development services',
      partyA: {
        name: 'Your Company Name',
        address: '123 Business St, City, State 12345',
        contact: 'legal@yourcompany.com',
        signatory: 'John Smith',
        title: 'CEO',
        taxId: '12-3456789'
      },
      partyB: {
        name: 'Client Company',
        address: '456 Client Ave, City, State 67890',
        contact: 'client@clientcompany.com',
        signatory: 'Jane Doe',
        title: 'CTO',
        taxId: '98-7654321'
      },
      generatedDate: new Date().toLocaleDateString(),
      generatedBy: 'Current User',
      templateVersion: '1.0',
      signatureDate: new Date().toLocaleDateString()
    };

    // Add type-specific data
    switch (contractType) {
      case 'NDA':
      case 'Confidentiality':
        return {
          ...baseData,
          contractName: 'Non-Disclosure Agreement',
          contractPurpose: 'Confidential information sharing',
          confidentialityPeriod: '5 years',
          noticePeriod: '30 days',
          governingLaw: 'State of California'
        };
      
      case 'Service Agreement':
      case 'MSA':
        return {
          ...baseData,
          contractName: 'Master Service Agreement',
          serviceDescription1: 'Software development',
          serviceDescription2: 'Technical support',
          serviceDescription3: 'Maintenance services',
          paymentTerms: 'Net 30',
          billingCycle: 'Monthly',
          latePaymentFee: '1.5',
          renewalTerm: '12 months',
          ipOwnership: 'Service Provider',
          liabilityLimit: '100000',
          governingLaw: 'State of California'
        };
      
      case 'SoW':
      case 'Statement of Work':
        return {
          ...baseData,
          contractName: 'Statement of Work',
          sowId: 'SOW-2024-001',
          relatedMSA: 'MSA-2024-001',
          projectName: 'Website Development Project',
          projectDescription: 'Development of a new company website',
          projectManager: 'John Smith',
          projectStartDate: '2024-01-15',
          projectEndDate: '2024-06-15',
          scopeItem1: 'Design and development of responsive website',
          scopeItem2: 'Content management system integration',
          scopeItem3: 'SEO optimization',
          scopeItem4: 'Testing and quality assurance',
          deliverable1: 'Website Design Mockups',
          deliverable1Date: '2024-02-15',
          deliverable1Description: 'Complete design mockups for all pages',
          deliverable2: 'Development Environment Setup',
          deliverable2Date: '2024-02-28',
          deliverable2Description: 'Staging environment with CMS integration',
          deliverable3: 'Final Website',
          deliverable3Date: '2024-06-15',
          deliverable3Description: 'Production-ready website with all features',
          milestone1: 'Design Approval',
          milestone1Date: '2024-02-15',
          milestone1Criteria: 'Client approval of all design mockups',
          milestone2: 'Development Complete',
          milestone2Date: '2024-05-15',
          milestone2Criteria: 'All functionality implemented and tested',
          teamMember1: 'John Smith',
          role1: 'Project Manager',
          teamMember2: 'Sarah Johnson',
          role2: 'Frontend Developer',
          teamMember3: 'Mike Wilson',
          role3: 'Backend Developer',
          clientResources: 'Content and images',
          projectValue: '75000',
          payment1Percentage: '25',
          payment2Percentage: '25',
          payment3Percentage: '25',
          payment4Percentage: '25',
          changeAnalysisTimeframe: '3 business days',
          acceptanceTimeframe: '5 business days',
          msaDate: '2024-01-01'
        };
      
      default:
        return baseData;
    }
  }

  // Generate file download URL
  getFileDownloadUrl(filePath) {
    if (!filePath) return null;
    
    // If it's already a full URL, return as is
    if (filePath.startsWith('http')) {
      return filePath;
    }
    
    // Construct the full URL using the API base URL
    const baseUrl = API_CONFIG.BASE_URL;
    return `${baseUrl}${filePath}`;
  }

  // Handle file download
  async downloadFile(url, filename) {
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error('Failed to download file');
      }
      
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Error downloading file:', error);
      throw error;
    }
  }
}

export default new TemplateService();
